import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useExportOffersToSearch } from '~/hooks/apis/cruises/useExportOffersToSearch';
import { useGetCruiseLines } from '~/hooks/apis/cruises/useGetCruiseLines';

import type { CruiseLine } from '~/services/cruises/CruiseLineService';
import type { ExportOffersToSearchResult } from '~/services/cruises/CruiseToolsService';

type VendorSyncRecord = Record<string, number>;

const ONE_HOUR_MS = 60 * 60 * 1000;
const SYNC_OFFERS_STORAGE_KEY = 'cruise-tools-sync-offers-by-vendor';

function readSyncRecords(): VendorSyncRecord {
  try {
    if (typeof window === 'undefined') return {};

    const storedValue = window.localStorage.getItem(SYNC_OFFERS_STORAGE_KEY);
    if (!storedValue) return {};

    const parsedValue = JSON.parse(storedValue);
    if (!parsedValue || typeof parsedValue !== 'object') return {};

    return Object.fromEntries(
      Object.entries(parsedValue).filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
    );
  } catch {
    return {};
  }
}

function writeSyncRecords(records: VendorSyncRecord): void {
  try {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(SYNC_OFFERS_STORAGE_KEY, JSON.stringify(records));
  } catch {
    // Local storage is a client-side guard only; do not block the queued sync if it is unavailable.
  }
}

function getVendorLabel(vendor: CruiseLine): string {
  if (vendor.code) return `${vendor.name} (${vendor.code})`;
  return vendor.name;
}

function SyncOffersPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedVendors, setSelectedVendors] = useState<Array<CruiseLine>>([]);
  const [shouldSkipPriceCrawler, setShouldSkipPriceCrawler] = useState(false);
  const [syncRecords, setSyncRecords] = useState<VendorSyncRecord>(() => readSyncRecords());

  const { data: vendors = [], isLoading: isLoadingVendors } = useGetCruiseLines();
  const exportOffersToSearch = useExportOffersToSearch();

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === SYNC_OFFERS_STORAGE_KEY) {
        setSyncRecords(readSyncRecords());
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => window.removeEventListener('storage', handleStorageEvent);
  }, []);

  const blockedVendors = useMemo(() => {
    const now = Date.now();
    return selectedVendors.filter((vendor) => {
      const lastSyncedAt = syncRecords[vendor.id];
      return Boolean(lastSyncedAt && now - lastSyncedAt < ONE_HOUR_MS);
    });
  }, [selectedVendors, syncRecords]);

  const selectedVendorIds = useMemo(() => selectedVendors.map((vendor) => vendor.id), [selectedVendors]);
  const blockedVendorNames = useMemo(() => blockedVendors.map(getVendorLabel).join(', '), [blockedVendors]);
  const isBlocked = blockedVendors.length > 0;
  const isSubmitDisabled = selectedVendors.length === 0 || isBlocked || exportOffersToSearch.isPending;

  const handleVendorChange = useCallback((_: SyntheticEvent, value: Array<CruiseLine>) => {
    setSelectedVendors(value);
  }, []);

  const handleSkipPriceCrawlerChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldSkipPriceCrawler(event.target.checked);
  }, []);

  const handleSyncOffers = useCallback(async () => {
    if (!selectedVendorIds.length || isBlocked) return;

    const latestSyncRecords = readSyncRecords();
    const now = Date.now();
    const hasRecentSync = selectedVendorIds.some((vendorId) => {
      const lastSyncedAt = latestSyncRecords[vendorId];
      return Boolean(lastSyncedAt && now - lastSyncedAt < ONE_HOUR_MS);
    });

    if (hasRecentSync) {
      setSyncRecords(latestSyncRecords);
      return;
    }

    let result: ExportOffersToSearchResult;

    try {
      result = await exportOffersToSearch.mutateAsync({
        vendorIds: selectedVendorIds,
        shouldSkipPriceCrawler,
      });
    } catch (error) {
      enqueueSnackbar(`Failed to sync offers: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        variant: 'error',
      });
      return;
    }

    const nextRecords = selectedVendorIds.reduce<VendorSyncRecord>(
      (records, vendorId) => ({
        ...records,
        [vendorId]: now,
      }),
      latestSyncRecords,
    );

    writeSyncRecords(nextRecords);
    setSyncRecords(nextRecords);
    enqueueSnackbar(`Sync queued for ${result.enqueuedOffers} offer(s).`, { variant: 'success' });
  }, [enqueueSnackbar, exportOffersToSearch, isBlocked, selectedVendorIds, shouldSkipPriceCrawler]);

  return (
    <Box>
      <PageHeader title="Sync offers" subtitle="Sync active cruise offers to customer search" />

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            <Alert severity="info">
              This action queues background jobs. Changes can take time to reflect in the customer portal.
            </Alert>

            <Autocomplete
              multiple
              options={vendors}
              value={selectedVendors}
              onChange={handleVendorChange}
              loading={isLoadingVendors}
              disableCloseOnSelect
              getOptionLabel={getVendorLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => (
                <Box component="li" {...props} key={option.id}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Stack>
                    <Typography variant="body2">{getVendorLabel(option)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.id}
                    </Typography>
                  </Stack>
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} label={getVendorLabel(option)} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendors"
                  placeholder={selectedVendors.length === 0 ? 'Select vendors' : undefined}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingVendors ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />

            <FormControlLabel
              control={<Checkbox checked={shouldSkipPriceCrawler} onChange={handleSkipPriceCrawlerChange} />}
              label="Skip price crawler"
            />

            {isBlocked && (
              <Alert severity="warning">
                Sync was run for {blockedVendorNames} less than one hour ago. Changes can take time to reflect in the
                customer portal.
              </Alert>
            )}

            <Box>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                disabled={isSubmitDisabled}
                onClick={handleSyncOffers}
              >
                {exportOffersToSearch.isPending ? 'Syncing...' : 'Sync offers'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SyncOffersPage;
